import { useQuery } from '@powersync/react';
import { useEffect } from 'react';

import { completeBlock, createBlock, reopenBlock, updateBlockTitle } from '@flow/domain';
import {
  applyLocalOperations,
  queries,
  type BlockListItem,
  type WorkspaceSummary,
} from '@flow/sync';

import { BlockList } from '../components/BlockList.jsx';
import type { LocalDatabase } from '../db/powersync.js';
import { useNextSortKey } from '../db/use-next-sort-key.js';
import { t } from '../i18n/es.js';
import { useSession } from '../session.jsx';
import { useSetViewHeader } from '../shell/AppLayout.jsx';

/**
 * Test list of F0, now inside the shell at `/blocks` until the Inbox and ASAP views arrive. The
 * layout has already waited for the session and the local database.
 */
export function HomePage() {
  const { user, local } = useSession();
  const setHeader = useSetViewHeader();
  useEffect(() => {
    setHeader({ title: t.nav.blocksScreen });
  }, [setHeader]);
  if (!user || !local) return null;

  return (
    <div className="page">
      <span className="muted" data-testid="startup" />
      <PersonalWorkspace local={local} userId={user.id} />
    </div>
  );
}

let startupMarked = false;

/**
 * Cold start of the test app (docs/12 §4, «Arranque en frío móvil hasta ASAP < 2 s»): time from
 * the page start to the database being ready and to the first block list, once per page load.
 * Shown in the header (and logged) so it can be read on a real iPhone without a debugger.
 */
function useStartupMark(ready: boolean, local: LocalDatabase) {
  useEffect(() => {
    if (!ready || startupMarked) return;
    startupMarked = true;
    const listMs = Math.round(performance.now());
    const element = document.querySelector<HTMLElement>('[data-testid="startup"]');
    if (element) {
      element.dataset.dbMs = String(local.readyMs);
      element.dataset.listMs = String(listMs);
      element.textContent = t.app.startup(local.readyMs, listMs);
    }
    console.info(`[flow] arranque: base lista en ${local.readyMs} ms · bloques en ${listMs} ms`);
  }, [ready, local]);
}

function PersonalWorkspace({ local, userId }: { local: LocalDatabase; userId: string }) {
  const workspaceQuery = queries.personalWorkspace();
  const { data: workspaces } = useQuery<WorkspaceSummary>(
    workspaceQuery.sql,
    workspaceQuery.params,
  );
  const workspace = workspaces[0];
  if (!workspace) return <p className="muted">{t.sync.firstSync}</p>;
  return <Blocks local={local} userId={userId} workspaceId={workspace.id} />;
}

function Blocks({
  local,
  userId,
  workspaceId,
}: {
  local: LocalDatabase;
  userId: string;
  workspaceId: string;
}) {
  const blocksQuery = queries.actionableBlocks(workspaceId);
  const stageQuery = queries.captureStage(workspaceId);
  const { data: blocks, isLoading } = useQuery<BlockListItem>(blocksQuery.sql, blocksQuery.params);
  useStartupMark(!isLoading, local);
  const { data: stages } = useQuery<{ id: string }>(stageQuery.sql, stageQuery.params);
  const nextSortKey = useNextSortKey(workspaceId);

  // Every write goes through a domain command and the local executor: no SQL here.
  const run = (operations: ReturnType<typeof createBlock>) =>
    applyLocalOperations(local.db, operations);

  return (
    <BlockList
      blocks={blocks}
      onCreate={(title) =>
        run(
          createBlock({
            workspace_id: workspaceId,
            created_by: userId,
            title,
            stage_id: stages[0]?.id ?? null,
            sort_key: nextSortKey(),
          }),
        )
      }
      onRename={(id, title) => run(updateBlockTitle({ id, title }))}
      onComplete={(block) =>
        run(
          completeBlock({
            block: { id: block.id, kind: block.kind as 'task', status: block.status as 'open' },
          }),
        )
      }
      onReopen={(block) =>
        run(
          reopenBlock({
            block: { id: block.id, kind: block.kind as 'task', status: block.status as 'done' },
          }),
        )
      }
    />
  );
}
